import { useCallback, useEffect, useRef, useState } from 'react'
import { COLORS } from '../constants'
import s from './ColorChooser.module.css'

type ColorChooserProps = {
    color: string
    onChange: (color: string) => void
}

const ColorChooser = ({ color, onChange }: ColorChooserProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const chooserRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!isOpen) {
            return
        }

        const onPointerDown = (event: PointerEvent) => {
            if (!chooserRef.current?.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false)
            }
        }

        document.addEventListener('pointerdown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)

        return () => {
            document.removeEventListener('pointerdown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [isOpen])

    const onColorSelect = useCallback(
        (newColor: string) => {
            onChange(newColor)
            setIsOpen(false)
        },
        [onChange]
    )

    return (
        <div className={s.colorChooser} ref={chooserRef}>
            <button
                type="button"
                className={s.colorChooserTrigger}
                style={{ backgroundColor: color }}
                onClick={() => setIsOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={isOpen}
                aria-label={`Selected color: ${color}`}
                title={color}
            ></button>
            {isOpen && (
                <div className={s.colorPopover} role="radiogroup" aria-label="item color">
                    {COLORS.map((c) => (
                        <button
                            key={c}
                            type="button"
                            role="radio"
                            aria-checked={color === c}
                            className={s.colorSwatch}
                            style={{ backgroundColor: c }}
                            onClick={() => onColorSelect(c)}
                            title={c}
                            aria-label={c}
                        ></button>
                    ))}
                </div>
            )}
        </div>
    )
}

export default ColorChooser
